/* A small router on the History API: the current URL as an external store, navigate(), and <Link>. */
import { useMemo, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { isAppPath } from '../lib/routes';

const listeners = new Set<() => void>();
const current = () => location.pathname + location.search + location.hash;
let snapshot = current();

function emit() {
  const next = current();
  if (next === snapshot) return;
  snapshot = next;
  listeners.forEach(l => l());
}
window.addEventListener('popstate', emit);

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/* Our pages change in place; anything else (the planner, the API, other sites) loads normally. */
export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  const url = new URL(to, location.href);
  if (url.origin !== location.origin || !isAppPath(url.pathname)) { location.assign(url.href); return; }
  const target = url.pathname + url.search + url.hash;
  if (opts.replace) history.replaceState(null, '', target); else history.pushState(null, '', target);
  emit();
}

/* change the address bar without leaving the page (e.g. dropping ?paid=1 or a #fragment) */
export function replaceUrl(to: string): void {
  history.replaceState(history.state, '', to);
  emit();
}

export type Loc = { href: string; pathname: string; search: string; hash: string; query: URLSearchParams };
export function useLocation(): Loc {
  const href = useSyncExternalStore(subscribe, () => snapshot);
  return useMemo(() => {
    const u = new URL(href, location.origin);
    return { href, pathname: u.pathname, search: u.search, hash: u.hash, query: u.searchParams };
  }, [href]);
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; children?: ReactNode };
export function Link({ to, onClick, ...rest }: LinkProps) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (rest.target || rest.download !== undefined) return;
    const url = new URL(to, location.href);
    if (url.origin !== location.origin || !isAppPath(url.pathname)) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };
  return <a href={to} {...rest} onClick={handle} />;
}
